import React from 'react';
import { observer } from 'mobx-react-lite';
import type { SvgElement } from '@/types';
import { ConnectionPoints } from './ConnectionPoints';

interface SvgElementProps {
  element: SvgElement;
  isSelected: boolean;
}

export const SvgElementComponent: React.FC<SvgElementProps> = observer(({ element, isSelected }) => {
  const { id, x, y, width, height, svgContent, style } = element;

  // Parse and process SVG content to ensure it scales properly
  const processedSvgContent = React.useMemo(() => {
    try {
      // Create a temporary div to parse the SVG content
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svgNode = doc.documentElement;
      
      if (svgNode.nodeName === 'svg') {
        // Clone the SVG and modify it to ensure proper scaling
        const clonedSvg = svgNode.cloneNode(true) as SVGSVGElement;
        
        // Remove fixed width/height and set viewBox if not present
        clonedSvg.removeAttribute('width');
        clonedSvg.removeAttribute('height');
        clonedSvg.setAttribute('width', '100%');
        clonedSvg.setAttribute('height', '100%');
        
        // Ensure there's a viewBox for proper scaling
        if (!clonedSvg.getAttribute('viewBox')) {
          // Try to get original dimensions for viewBox
          const originalWidth = svgNode.getAttribute('width') || '100';
          const originalHeight = svgNode.getAttribute('height') || '100';
          clonedSvg.setAttribute('viewBox', `0 0 ${parseFloat(originalWidth)} ${parseFloat(originalHeight)}`);
        }
        
        // Set preserveAspectRatio to ensure the SVG scales properly
        clonedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        return clonedSvg.outerHTML;
      }
      
      // If it's not a complete SVG, wrap it
      return `<svg viewBox="0 0 100 100" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`;
    } catch (error) {
      console.error('Failed to parse SVG content:', error);
      return '<svg viewBox="0 0 100 100" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><text x="50%" y="50%" text-anchor="middle" fill="red">Invalid SVG</text></svg>';
    }
  }, [svgContent]);

  return (
    <g>
      {/* SVG content container */}
      <g clipPath={`url(#svg-clip-${id})`}>
        <defs>
          <clipPath id={`svg-clip-${id}`}>
            <rect x={x} y={y} width={width} height={height} />
          </clipPath>
        </defs>
        
        {/* Render the SVG content */}
        <foreignObject
          x={x}
          y={y}
          width={width}
          height={height}
          opacity={style.opacity}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            dangerouslySetInnerHTML={{
              __html: processedSvgContent
            }}
          />
        </foreignObject>
        
        {/* Invisible clickable area */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          style={{ cursor: 'move' }}
        />
      </g>

      {/* Connection points */}
      <ConnectionPoints
        element={element}
        zoom={1}
        isHovered={isSelected}
      />

      {/* Selection indicators */}
      {isSelected && (
        <g>
          {/* Selection border */}
          <rect
            x={x - 2}
            y={y - 2}
            width={width + 4}
            height={height + 4}
            fill="none"
            stroke="#007acc"
            strokeWidth={2}
            strokeDasharray="5,5"
            rx={4}
          />
          {/* Resize handles */}
          <rect x={x - 6} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x - 6} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width/2 - 4} y={y - 6} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width/2 - 4} y={y + height - 2} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x - 6} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
          <rect x={x + width - 2} y={y + height/2 - 4} width={8} height={8} fill="#007acc" stroke="#ffffff" strokeWidth={1} />
        </g>
      )}
    </g>
  );
}); 